// src/pages/AccountPage.jsx
// Everything about the person's account in one place: who they are, how they sign in, password and address, their data (a
// copy to take away), this device's offline copy, privacy settings, signing out and deleting the account.
import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { consentChoice, setConsent, subscribeToConsent } from '../analytics';
import { apiRequest } from '../api';
import {
    changeDisplayName,
    changePassword,
    displayNameOf,
    requestEmailChange,
    signInMethodsOf,
    subscribeToAuth,
    usesPassword,
} from '../auth';
import AppShell, { initialsOf } from '../components/AppShell';
import DeleteAccount from '../components/DeleteAccount';
import { ArrowLeftIcon, DownloadIcon } from '../components/icons';
import { useToast } from '../components/Toast';
import { UserContext } from '../context/UserContext';
import { isNativeApp } from '../native';
import { clearAppCache } from '../serviceWorkerRegistration';
import { isTestMode } from '../testMode/testMode';
import { useSync } from '../sync/SyncProvider';
import { formatExact } from '../utils/time';
import { downloadBlob } from '../utils/resultsExport';
import { signInErrorMessage } from './Login';

const explain = (error) => (error.code ? signInErrorMessage(error) : error.message || 'Something went wrong.');

function Profile({ user }) {
    const toast = useToast();
    const [name, setName] = useState(user.displayName || '');
    const [busy, setBusy] = useState(false);
    const changed = name.trim() !== (user.displayName || '') && name.trim() !== '';

    const save = async (event) => {
        event.preventDefault();
        setBusy(true);
        try {
            await changeDisplayName(name);
            toast.success('Name saved.');
        } catch (error) {
            toast.error(`Could not save the name. ${explain(error)}`);
        }
        setBusy(false);
    };

    return (
        <section className="card account-section" aria-label="Profile">
            <h2>Profile</h2>
            <div className="account-who">
                <span className="avatar big" aria-hidden="true">{initialsOf(displayNameOf(user))}</span>
                <div>
                    <strong>{displayNameOf(user)}</strong>
                    <div className="muted">{user.email}</div>
                    <div className="hint">
                        Signs in with {signInMethodsOf(user).join(' and ') || 'Google'}
                        {user.emailVerified ? ' · address confirmed' : ''}
                    </div>
                </div>
            </div>
            <form className="account-form" onSubmit={save}>
                <label htmlFor="account-name" className="hint">
                    Your name (shown to co-organizers and in the score history)
                </label>
                <div className="account-row">
                    <input id="account-name" className="input" value={name} maxLength={60} onChange={(event) => setName(event.target.value)} />
                    <button className="btn btn-primary" type="submit" disabled={busy || !changed}>
                        Save
                    </button>
                </div>
            </form>
        </section>
    );
}

function Security({ user }) {
    const toast = useToast();
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [address, setAddress] = useState('');
    const [addressPassword, setAddressPassword] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    if (!usesPassword(user)) {
        return (
            <section className="card account-section" aria-label="Security">
                <h2>Sign-in</h2>
                <p className="hint">
                    You sign in with {signInMethodsOf(user).join(' and ') || 'Google'}, so your password and email address are
                    managed in that account. Only they can change them.
                </p>
            </section>
        );
    }

    const run = async (action, done) => {
        setBusy(true);
        setError(null);
        try {
            await action();
            toast.success(done);
            return true;
        } catch (failure) {
            console.error('Account change failed:', failure);
            setError(explain(failure));
            return false;
        } finally {
            setBusy(false);
        }
    };

    const submitPassword = async (event) => {
        event.preventDefault();
        if (await run(() => changePassword(current, next), 'Password changed.')) {
            setCurrent('');
            setNext('');
        }
    };

    const submitAddress = async (event) => {
        event.preventDefault();
        if (await run(() => requestEmailChange(addressPassword, address), `A confirmation link was sent to ${address.trim()}. The address changes once you open it.`)) {
            setAddress('');
            setAddressPassword('');
        }
    };

    return (
        <section className="card account-section" aria-label="Security">
            <h2>Password and email address</h2>
            <form className="account-form" onSubmit={submitPassword} aria-label="Change password">
                <h3>Change password</h3>
                <input className="input" type="password" autoComplete="current-password" placeholder="Current password" aria-label="Current password" value={current} onChange={(event) => setCurrent(event.target.value)} required />
                <input className="input" type="password" autoComplete="new-password" placeholder="New password (at least 6 characters)" aria-label="New password" value={next} onChange={(event) => setNext(event.target.value)} required minLength={6} />
                <button className="btn" type="submit" disabled={busy}>
                    Change password
                </button>
            </form>
            <form className="account-form" onSubmit={submitAddress} aria-label="Change email address">
                <h3>Change email address</h3>
                <input className="input" type="email" autoComplete="email" placeholder="New email address" aria-label="New email address" value={address} onChange={(event) => setAddress(event.target.value)} required />
                <input className="input" type="password" autoComplete="current-password" placeholder="Your password" aria-label="Your password to change the address" value={addressPassword} onChange={(event) => setAddressPassword(event.target.value)} required />
                <button className="btn" type="submit" disabled={busy}>
                    Send confirmation link
                </button>
            </form>
            {error && (
                <p className="form-error" role="alert">
                    {error}
                </p>
            )}
        </section>
    );
}

function YourData({ user }) {
    const toast = useToast();
    const [counts, setCounts] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let alive = true;
        apiRequest('GET', '/getTournaments', user.uid)
            .then((list) => alive && setCounts({ tournaments: list.length, shared: list.filter((t) => t.shared).length }))
            .catch(() => {});
        return () => {
            alive = false;
        };
    }, [user.uid]);

    const download = async () => {
        setBusy(true);
        try {
            const data = await apiRequest('GET', '/account/export', user.uid);
            downloadBlob(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }), `tournamental-data-${new Date().toISOString().slice(0, 10)}.json`);
        } catch (error) {
            toast.error(`Could not collect your data. ${error.message}`);
        }
        setBusy(false);
    };

    return (
        <section className="card account-section" aria-label="Your data">
            <h2>Your data</h2>
            <p className="hint">
                {counts === null
                    ? 'Loading…'
                    : `${counts.tournaments} tournament${counts.tournaments === 1 ? '' : 's'}${counts.shared ? `, ${counts.shared} of them run with others` : ''}.`}{' '}
                A copy of everything (tournaments, players, rounds, scores, ranking and score history) as a readable file:
            </p>
            <button className="btn" onClick={download} disabled={busy}>
                <DownloadIcon size={16} /> Download my data
            </button>
            {!user.isGuest && (
                <p className="hint">
                    Backups of your account are under <Link to="/dashboard">Backups</Link> on the dashboard.
                </p>
            )}
        </section>
    );
}

function Device({ user }) {
    const sync = useSync();
    const toast = useToast();
    const [busy, setBusy] = useState(false);
    if (user.isGuest) return null;

    const syncNow = async () => {
        setBusy(true);
        const result = await sync.syncNow?.();
        setBusy(false);
        if (result && ['idle', 'pulled', 'pushed', 'merged'].includes(result.outcome)) toast.success('Up to date.');
        else if (result && result.outcome === 'offline') toast.info('No connection: your changes are kept on this device and sent later.');
    };

    return (
        <section className="card account-section" aria-label="This device">
            <h2>This device</h2>
            {sync.enabled ? (
                <>
                    <p className="hint">
                        Your tournaments work without a connection on this device. Changes are sent to the server and to your
                        other devices when you are online.
                        {sync.lastSynced ? ` Last synced ${formatExact(sync.lastSynced.toISOString())}.` : ''}
                    </p>
                    <button className="btn" onClick={syncNow} disabled={busy || sync.phase === 'syncing'}>
                        {busy || sync.phase === 'syncing' ? 'Syncing…' : 'Sync now'}
                    </button>
                </>
            ) : (
                <p className="hint">Offline use is being prepared on this device (it needs a connection once).</p>
            )}
        </section>
    );
}

function AppVersion() {
    const [busy, setBusy] = useState(false);
    if (isNativeApp()) return null;
    return (
        <section className="card account-section" aria-label="App version">
            <h2>App version</h2>
            <p className="hint">
                Seeing an old version of the app? This clears the saved app files on this device and loads the newest version (needs a connection).
                Your tournaments and your sign-in are kept.
            </p>
            <button
                className="btn"
                disabled={busy}
                onClick={() => {
                    setBusy(true);
                    clearAppCache();
                }}
            >
                {busy ? 'Reloading…' : 'Clear cache and reload'}
            </button>
        </section>
    );
}

function Privacy() {
    const [choice, setChoice] = useState(consentChoice());
    useEffect(() => subscribeToConsent(setChoice), []);
    return (
        <section className="card account-section" aria-label="Privacy">
            <h2>Privacy</h2>
            <p className="hint">
                Anonymous usage statistics (Google Analytics):{' '}
                <strong>{choice === 'granted' ? 'allowed' : choice === 'denied' ? 'not allowed' : 'not decided yet'}</strong>.
            </p>
            <div className="account-row">
                {choice !== 'granted' && (
                    <button className="btn" onClick={() => setConsent('granted')}>
                        Allow
                    </button>
                )}
                {choice !== 'denied' && (
                    <button className="btn" onClick={() => setConsent('denied')}>
                        Do not allow
                    </button>
                )}
            </div>
            <p className="hint">
                <Link to="/privacy">Privacy policy</Link> · <Link to="/terms">Terms of use</Link> · <Link to="/help">Help</Link>
            </p>
        </section>
    );
}

function AccountPage() {
    const context = useContext(UserContext);
    const navigate = useNavigate();
    const [user, setUser] = useState(context);

    // Signed out (or the session ended): back to the login page.
    useEffect(
        () =>
            subscribeToAuth((authUser) => {
                if (!authUser) navigate('/');
                else setUser(authUser);
            }),
        [navigate]
    );

    useEffect(() => {
        document.title = 'Account · Tournamental';
    }, []);

    if (!user) return null;

    return (
        <AppShell user={user}>
            <div className="page-head">
                <button className="btn btn-ghost btn-sm back-link" onClick={() => navigate('/dashboard')}>
                    <ArrowLeftIcon size={16} /> Back to dashboard
                </button>
            </div>
            <h1>Account</h1>

            {user.isGuest ? (
                <section className="card account-section" aria-label="Guest">
                    <h2>You are a guest</h2>
                    <p className="hint">
                        Your tournaments are only on this device. Sign in or create an account to keep them safe, use several
                        devices and run tournaments with others; you can then add what you made as a guest to the account.
                    </p>
                    <button className="btn btn-primary" onClick={() => navigate('/')}>
                        Sign in or create an account
                    </button>
                </section>
            ) : (
                <>
                    <Profile user={user} />
                    <Security user={user} />
                </>
            )}

            <YourData user={user} />
            <Device user={user} />
            <AppVersion />
            <Privacy />

            {!isTestMode() && <DeleteAccount user={user} />}
        </AppShell>
    );
}

export default AccountPage;
